import { oauthAuth } from "../standardAuthPatterns";
import { caCertificateItem, defineConnector } from "../types";
import logo from "./logo.svg";

const grantTypeTarget = {
  kind: "configuration" as const,
  field: "grantType",
} as const;

const tokenAuthMethodTarget = {
  kind: "configuration" as const,
  field: "tokenAuthMethod",
} as const;

export const customOauth = defineConnector({
  identifier: "customOauth",
  name: "OAuth 2.0",
  tagLine: "Bring your own provider",
  logo,
  protocol: "http",
  tags: ["custom"],
  defaultUrlPattern: () => ".*",
  customizeStepHandledInline: true,
  auth: oauthAuth,
  oauth: {
    grantType: (cfg) =>
      cfg.grantType === "client_credentials"
        ? "client_credentials"
        : "authorization_code",
    clientCredentialsFrom: "db",
    authorizationEndpoint: (cfg) => cfg.authorizeUrl ?? "",
    tokenEndpoint: (cfg) => cfg.tokenUrl ?? "",
    issuer: (cfg) => cfg.issuer ?? "",
    tokenEndpointAuthMethod: (cfg) =>
      cfg.tokenAuthMethod === "client_secret_basic" ||
      cfg.tokenAuthMethod === "none"
        ? cfg.tokenAuthMethod
        : "client_secret_post",
    // OAuth 2.1 makes PKCE mandatory for public clients
    pkce: (cfg) => cfg.pkce === "on" || cfg.tokenAuthMethod === "none",
    scopes: (cfg) => (cfg.scopes ?? "").split(/\s+/).filter(Boolean),
    tokenRequestFormat: (cfg) =>
      cfg.tokenRequestFormat === "json" ? "json" : "form",
    authorizationParameters: (cfg): Record<string, string> =>
      cfg.audience ? { audience: cfg.audience } : {},
    resource: (cfg) => cfg.resource ?? "",
  },
  steps: [
    {
      title: "Name your connection",
      items: [
        {
          kind: "input",
          label: "Name",
          placeholder: "e.g. Acme SSO",
          target: { kind: "connectedApp", field: "name" },
        },
      ],
    },
    {
      title: "URLs",
      items: [
        {
          kind: "url-pattern",
          placeholder: "api.example.com",
          target: { kind: "connectedApp", field: "urlPattern" },
        },
        {
          kind: "test-url",
          label: "Test URL",
          placeholder: "https://api.example.com/user",
          helperText: "Leave blank to skip testing.",
          urlTarget: { kind: "configuration", field: "testUrl" },
          methodTarget: { kind: "configuration", field: "testMethod" },
        },
        caCertificateItem,
      ],
    },
    {
      title: "Configuration",
      items: [
        {
          kind: "select",
          label: "Grant type",
          target: grantTypeTarget,
          defaultValue: "authorization_code",
          options: [
            { value: "authorization_code", label: "Authorization code" },
            { value: "client_credentials", label: "Client credentials" },
          ],
        },
        {
          kind: "markdown",
          content:
            "Register an OAuth 2.0 application with the upstream provider and copy the redirect URI below into its allowed callbacks.",
        },
        {
          kind: "copyable",
          label: "Redirect URI",
          value: "{API_URL}/connected-apps/oauth/customOauth/callback",
        },
        {
          kind: "fullUrl",
          label: "Authorization URL",
          placeholder: "https://provider.example.com/oauth/authorize",
          target: { kind: "configuration", field: "authorizeUrl" },
          visibleWhen: [
            { target: grantTypeTarget, value: "authorization_code" },
          ],
        },
        {
          kind: "baseUrl",
          label: "Token URL",
          placeholder: "https://provider.example.com/oauth/token",
          target: { kind: "configuration", field: "tokenUrl" },
        },
        {
          kind: "select",
          label: "Token request format",
          target: { kind: "configuration", field: "tokenRequestFormat" },
          defaultValue: "form",
          options: [
            { value: "form", label: "Form encoded" },
            { value: "json", label: "JSON" },
          ],
          helperText:
            "Choose JSON only when the provider’s token endpoint requires it.",
        },
        {
          kind: "input",
          label: "Customize issuer URL (optional)",
          placeholder: "https://provider.example.com/",
          helperText:
            "Most providers can leave this blank. If required, copy the issuer URL from your provider’s OpenID configuration.",
          target: { kind: "configuration", field: "issuer" },
        },
        {
          kind: "input",
          label: "Scopes",
          placeholder: "read:user write:user",
          helperText:
            "Space-separated. Leave blank if the provider doesn’t use scopes.",
          target: { kind: "configuration", field: "scopes" },
        },
        {
          kind: "input",
          label: "Audience",
          placeholder: "https://api.example.com",
          helperText:
            "Sent as the audience parameter to the token endpoint. Leave blank if the provider doesn’t use it.",
          target: { kind: "configuration", field: "audience" },
        },
        {
          kind: "input",
          label: "Resource",
          placeholder: "https://mcp.example.com/mcp",
          helperText:
            "Sent as the RFC 8707 resource parameter on authorize and token requests. Required by some providers, including MCP servers. Leave blank if the provider doesn’t use it.",
          target: { kind: "configuration", field: "resource" },
        },
        {
          kind: "select",
          label: "Token endpoint authentication",
          target: tokenAuthMethodTarget,
          defaultValue: "client_secret_post",
          options: [
            {
              value: "client_secret_post",
              label: "Client secret in POST body",
            },
            {
              value: "client_secret_basic",
              label: "Client secret as HTTP Basic",
            },
            {
              value: "none",
              label: "None (public client with PKCE)",
            },
          ],
        },
        {
          kind: "select",
          label: "PKCE",
          target: { kind: "configuration", field: "pkce" },
          defaultValue: "off",
          options: [
            { value: "off", label: "Disabled" },
            { value: "on", label: "Enabled" },
          ],
          helperText:
            "Enable if the provider requires it. Always on for public clients.",
          visibleWhen: [
            { target: grantTypeTarget, value: "authorization_code" },
          ],
        },
        {
          kind: "input",
          label: "Client ID",
          placeholder: "Paste your client ID",
          target: { kind: "oauthConnection", field: "clientId" },
        },
        {
          kind: "input",
          label: "Client secret",
          placeholder: "Paste your client secret",
          target: { kind: "oauthConnection", field: "clientSecret" },
          visibleWhen: [{ target: tokenAuthMethodTarget, notValue: "none" }],
        },
        {
          kind: "oauth",
          dependsOn: [
            { kind: "configuration", field: "authorizeUrl" },
            { kind: "configuration", field: "tokenUrl" },
            { kind: "oauthConnection", field: "clientId" },
            { kind: "oauthConnection", field: "clientSecret" },
          ],
        },
      ],
    },
  ],
  connectionTest: {
    protocol: "http",
    request: ({ testUrl, testMethod }) => ({
      url: testUrl ?? "",
      method: testMethod === "POST" ? "POST" : "GET",
    }),
  },
});
